/**
 * Was eine Sammelbearbeitung je Tauchgang schreiben muss.
 *
 * Bewusst ohne `document` und ohne Datenbank: Hier wird nur gerechnet.
 * Geschrieben wird über `tabelle.js:schreibeZelle()`, damit die
 * Sammelbearbeitung denselben Weg nimmt wie eine einzelne Zelle — samt
 * Stammsatz-Anlage für Platz und Reise, Flaschenzuordnung und Zeitstempel.
 *
 * Die Werte sind darum Zeichenketten: genau das, was eine Zelle liefern
 * würde. `''` heisst "leeren", ein fehlender Schlüssel heisst "nicht anfassen".
 */

import { register, FLASCHE } from './felder.js';

const schluesselform = (name) => String(name ?? '').trim().toLowerCase();

/**
 * Die neue Buddy-Liste eines Tauchgangs.
 *
 * Verglichen wird ohne Rücksicht auf Gross- und Kleinschreibung — dieselbe
 * Regel wie beim Zählen in `tabelle.js`. Steht ein Name schon in anderer
 * Schreibweise da, bleibt die bestehende: Ein Sammelvorgang schreibt keine
 * Schreibweisen um.
 */
export function buddysNeu(bisher, genannt, modus = 'hinzufuegen') {
  const alt = Array.isArray(bisher) ? [...bisher] : [];
  const namen = String(genannt ?? '').split(',')
    .map((n) => n.trim()).filter(Boolean);

  if (modus === 'ersetzen') return namen;
  if (modus === 'entfernen') {
    const weg = new Set(namen.map(schluesselform));
    return alt.filter((n) => !weg.has(schluesselform(n)));
  }
  const vorhanden = new Set(alt.map(schluesselform));
  return [...alt, ...namen.filter((n) => !vorhanden.has(schluesselform(n)))];
}

const leer = (wert) => wert === undefined || wert === null || String(wert).trim() === '';

/** Was in der Zelle stünde — zum Vergleich, ob sich überhaupt etwas ändert. */
function bisher(satz, schluessel) {
  if (schluessel === 'buddies') return (satz.buddies ?? []).join(', ');
  if (schluessel in FLASCHE) {
    const wert = satz.tanks?.[0]?.[FLASCHE[schluessel]];
    return wert === null || wert === undefined ? '' : String(wert);
  }
  const wert = satz[schluessel];
  return wert === null || wert === undefined ? '' : String(wert);
}

export function sammelAenderungen(tauchgaenge, eingaben) {
  const { werte = {}, leeren = [], buddyModus = 'hinzufuegen' } = eingaben ?? {};
  const spalten = register('dives')?.spalten ?? [];
  const bearbeitbar = new Set(spalten.filter((s) => s.bearbeitbar).map((s) => s.schluessel));
  // Geleert wird nur, was von Hand gepflegt wird. Importiertes lässt sich
  // überschreiben, aber nicht entfernen — siehe Nachtrag im Plan.
  const leerbar = new Set(spalten.filter((s) => s.bearbeitbar && s.gepflegt)
    .map((s) => s.schluessel));
  const zuLeeren = new Set(leeren.filter((s) => leerbar.has(s)));

  // Reihenfolge der Spalten, nicht die des Eingabeobjekts: So steht in der
  // Übersicht dasselbe wie im Formular.
  const betroffen = spalten
    .map((s) => s.schluessel)
    .filter((s) => bearbeitbar.has(s) && (zuLeeren.has(s) || !leer(werte[s])));

  const ergebnis = [];
  for (const satz of tauchgaenge) {
    const felder = [];
    for (const schluessel of betroffen) {
      const alt = bisher(satz, schluessel);
      let wert = zuLeeren.has(schluessel) ? '' : String(werte[schluessel]);
      if (schluessel === 'buddies' && !zuLeeren.has(schluessel)) {
        wert = buddysNeu(satz.buddies, wert, buddyModus).join(', ');
      }
      if (wert !== alt) felder.push({ schluessel, wert, vorher: alt });
    }
    if (felder.length) ergebnis.push({ satz, felder });
  }
  return ergebnis;
}

/** Felder, die am Platz hängen und nicht am Tauchgang. */
export const PLATZFELDER = ['country', 'region', 'water_name'];

/**
 * Was eine Änderung an den Plätzen der Markierung anrichtet.
 *
 * Der Benutzer denkt in Tauchgängen. Ein Platz gehört aber auch Tauchgängen,
 * die er nicht markiert hat — diese Zahl gehört ihm vor Augen, nicht in eine
 * Fussnote.
 */
export function platzWirkung(markierte, alleTauchgaenge) {
  const platzIds = new Set(markierte.map((tg) => tg.site_id).filter(Boolean));
  const markierteIds = new Set(markierte.map((tg) => tg.id));

  let mitFremden = 0;
  let fremdeTauchgaenge = 0;
  for (const platzId of platzIds) {
    const fremde = alleTauchgaenge.filter((tg) => tg.site_id === platzId
      && !markierteIds.has(tg.id)).length;
    if (fremde > 0) mitFremden += 1;
    fremdeTauchgaenge += fremde;
  }
  return { plaetze: platzIds.size, mitFremden, fremdeTauchgaenge };
}

/** Dieselbe Rechnung wie `sammelAenderungen`, aber für die Plätze dahinter. */
export function platzAenderungen(markierte, plaetze, eingaben) {
  const { werte = {}, leeren = [] } = eingaben ?? {};
  const zuLeeren = new Set(leeren.filter((s) => PLATZFELDER.includes(s)));
  const betroffen = PLATZFELDER
    .filter((s) => zuLeeren.has(s) || !leer(werte[s]));
  if (betroffen.length === 0) return [];

  const platzIds = new Set(markierte.map((tg) => tg.site_id).filter(Boolean));
  const ergebnis = [];
  for (const platz of plaetze) {
    if (!platzIds.has(platz.id)) continue;
    const felder = [];
    for (const schluessel of betroffen) {
      const alt = bisher(platz, schluessel);
      const wert = zuLeeren.has(schluessel) ? '' : String(werte[schluessel]);
      if (wert !== alt) felder.push({ schluessel, wert, vorher: alt });
    }
    if (felder.length) ergebnis.push({ satz: platz, felder });
  }
  return ergebnis;
}

/**
 * Eine Änderungsliste in die Gegenrichtung drehen.
 *
 * Der alte Wert wird zum neuen. Weil er dieselbe Form hat wie ein gewöhnlicher
 * Sammelvorgang, läuft die Rücknahme durch denselben Schreibweg — es gibt
 * keinen zweiten, der `geaendert` oder den Abgleich umgehen könnte.
 */
export function umkehrung(aenderungen) {
  return aenderungen.map(({ satz, felder }) => ({
    satz,
    felder: felder.map(({ schluessel, wert, vorher }) =>
      ({ schluessel, wert: vorher, vorher: wert })),
  }));
}
